import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useMyTasks } from '../hooks/useApprovalCenter';
import { useMyRequests } from '../hooks/useApprovalCenter';
import { useApprovalInbox } from '../hooks/useApprovalCenter';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useHasPermission } from '../../_shared/hooks/useCurrentUser';
import { ApprovalCommonUsagePanel } from '../components/ApprovalCommonUsagePanel';
import type { ApprovalStatus, ApprovalSearchCondition, ApiError } from '../types/approvalCenter';
import type { StatusTone } from '@ui-kit';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_FILTERS: ApprovalSearchCondition = {
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  sort: 'submittedAt,desc',
};

function statusTone(status: ApprovalStatus): StatusTone {
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  if (status === 'CANCELLED') return 'warning';
  return 'info';
}

function formatDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i % 3) * 15}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

type TabKey = 'tasks' | 'mine' | 'admin';

/**
 * ApprovalInboxPage — 결재 센터 메인 (결재함)
 * 라우트: /approval-center
 * 권한: AUTHENTICATED (ROLE_USER+)
 * 탭: 받은 결재 | 올린 결재 | 운영 목록 (APPROVAL_READ 보유 시)
 */
export function ApprovalInboxPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canReadAll = useHasPermission('APPROVAL_READ');
  const [activeTab, setActiveTab] = useState<TabKey>('tasks');
  const [adminFilters, setAdminFilters] = useState<ApprovalSearchCondition>(DEFAULT_FILTERS);
  const [mineFilters, setMineFilters] = useState<ApprovalSearchCondition>(DEFAULT_FILTERS);
  const [adminPage, setAdminPage] = useState(0);
  const [minePage, setMinePage] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const tasksQuery = useMyTasks();
  const mineQuery = useMyRequests(mineFilters);
  // #648 §R648-2: canReadAll=false(APPROVAL_READ 미보유) 시 쿼리 미발사 — 탭 숨김과 동시 가드(permission-first)
  const adminQuery = useApprovalInbox(adminFilters, canReadAll);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleAdminPageChange = useCallback((p: number) => {
    setAdminPage(p);
    setAdminFilters((prev) => ({ ...prev, page: p }));
  }, []);

  const handleMinePageChange = useCallback((p: number) => {
    setMinePage(p);
    setMineFilters((prev) => ({ ...prev, page: p }));
  }, []);

  const taskCount = tasksQuery.data?.length ?? 0;

  // 받은 결재 탭 컬럼 헤더
  const taskColumns = [
    t('approval.inbox.column.form'),
    t('approval.inbox.column.title'),
    t('approval.inbox.column.requester'),
    t('approval.inbox.column.currentStep'),
    t('approval.inbox.column.submittedAt'),
    t('approval.inbox.column.action'),
  ];

  // 올린 결재 / 운영 목록 컬럼 헤더
  const requestColumns = [
    t('approval.inbox.column.form'),
    t('approval.inbox.column.title'),
    t('approval.inbox.column.requester'),
    t('approval.inbox.column.status'),
    t('approval.inbox.column.submittedAt'),
  ];

  const tabs: { key: TabKey; label: string; hidden?: boolean }[] = useMemo(
    () => [
      {
        key: 'tasks',
        label: `${t('approval.inbox.tab.tasks')}${taskCount > 0 ? ` (${taskCount})` : ''}`,
      },
      { key: 'mine', label: t('approval.inbox.tab.mine') },
      { key: 'admin', label: t('approval.inbox.tab.admin'), hidden: !canReadAll },
    ],
    [t, taskCount, canReadAll]
  );

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('approval.inbox.title')}
        breadcrumb={<span>{t('approval.inbox.title')}</span>}
        actions={
          <ButtonGuard requirePermission="APPROVAL_REQUEST">
            {({ disabled, reason }) => (
              <button
                type="button"
                disabled={disabled}
                title={reason}
                onClick={() => navigate('/approval-center/new')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                {t('approval.inbox.button.new')}
              </button>
            )}
          </ButtonGuard>
        }
      />

      {/* 탭 내비게이션 */}
      <div
        role="tablist"
        aria-label={t('approval.inbox.title')}
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid var(--color-surface-border)',
          marginBottom: 'var(--spacing-4)',
          overflowX: 'auto',
        }}
      >
        {tabs
          .filter((tab) => !tab.hidden)
          .map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`tabpanel-${tab.key}`}
              id={`tab-${tab.key}`}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.key
                  ? '2px solid var(--color-brand-primary)'
                  : '2px solid transparent',
                marginBottom: -2,
                fontSize: 'var(--font-size-sm)',
                fontWeight: activeTab === tab.key ? 700 : 400,
                color: activeTab === tab.key ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
      </div>

      {/* 탭 패널 — 받은 결재 */}
      {activeTab === 'tasks' && (
        <div
          id="tabpanel-tasks"
          role="tabpanel"
          aria-labelledby="tab-tasks"
        >
          <div
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: 'var(--color-surface-background)',
            }}
          >
            {/* Loading */}
            {tasksQuery.isLoading && (
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('approval.inbox.tab.tasks')}
                aria-busy="true"
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {taskColumns.map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} cols={6} />
                  ))}
                </tbody>
              </table>
            )}

            {/* 오류 */}
            {tasksQuery.isError && (
              <div style={{ padding: 'var(--spacing-6)' }}>
                <ErrorState
                  title={t('approval.error.network')}
                  message={(tasksQuery.error as unknown as ApiError)?.message ?? t('approval.error.network')}
                  onRetry={() => void tasksQuery.refetch()}
                />
              </div>
            )}

            {/* 빈 상태 */}
            {!tasksQuery.isLoading && !tasksQuery.isError && (tasksQuery.data?.length ?? 0) === 0 && (
              <div style={{ padding: 'var(--spacing-6)' }}>
                <EmptyState
                  title={t('approval.inbox.empty.tasks')}
                  description={t('approval.inbox.empty.tasks')}
                />
              </div>
            )}

            {/* 기본 — DataTable */}
            {!tasksQuery.isLoading && !tasksQuery.isError && (tasksQuery.data?.length ?? 0) > 0 && (
              <div role="region" aria-label={t('approval.inbox.tab.tasks')} style={{ overflowX: 'auto' }}>
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                  aria-label={t('approval.inbox.tab.tasks')}
                >
                  <thead>
                    <tr style={{ background: 'var(--color-surface-muted)' }}>
                      {taskColumns.map((h) => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tasksQuery.data?.map((task) => (
                      <tr
                        key={task.taskId}
                        tabIndex={0}
                        role="button"
                        aria-label={`${task.title} ${t('approval.inbox.button.open')}`}
                        onClick={() => navigate(`/approval-center/${task.requestId}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/approval-center/${task.requestId}`);
                          }
                        }}
                        style={rowStyle}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = '';
                        }}
                      >
                        <td style={tdStyle}>
                          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            {task.formLabel}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                            {task.title}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ color: 'var(--color-text-secondary)' }}>
                            {task.requesterName ?? task.requestedBy}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 600,
                              color: 'var(--color-brand-primary)',
                              background: 'color-mix(in srgb, var(--color-brand-primary) 10%, transparent)',
                              padding: '2px var(--spacing-2)',
                              borderRadius: 'var(--radius-sm)',
                            }}
                          >
                            {task.stepLabel}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
                            {formatDate(task.createdAt, i18n.language)}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/approval-center/${task.requestId}`);
                            }}
                            style={{
                              padding: 'var(--spacing-1) var(--spacing-3)',
                              background: 'var(--color-brand-primary)',
                              color: 'var(--color-text-inverse)',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontFamily: 'var(--font-family-sans)',
                            }}
                          >
                            {t('approval.inbox.button.open')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 탭 패널 — 올린 결재 */}
      {activeTab === 'mine' && (
        <div id="tabpanel-mine" role="tabpanel" aria-labelledby="tab-mine">
          <RequestsTable
            query={mineQuery}
            columns={requestColumns}
            emptyKey="approval.inbox.empty.mine"
            page={minePage}
            onPageChange={handleMinePageChange}
            onRowClick={(id) => navigate(`/approval-center/${id}`)}
            i18nLocale={i18n.language}
            t={t}
            showToast={showToast}
          />
        </div>
      )}

      {/* 탭 패널 — 운영 목록 (APPROVAL_READ) */}
      {activeTab === 'admin' && canReadAll && (
        <div id="tabpanel-admin" role="tabpanel" aria-labelledby="tab-admin">
          {/* 간단한 상태 필터 */}
          <div style={{ marginBottom: 'var(--spacing-3)', display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
            {(['', 'PENDING', 'APPROVED', 'REJECTED'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setAdminPage(0);
                  setAdminFilters({ ...DEFAULT_FILTERS, status: s || undefined });
                }}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  background: adminFilters.status === (s || undefined)
                    ? 'var(--color-brand-primary)'
                    : 'var(--color-surface-elevated)',
                  color: adminFilters.status === (s || undefined)
                    ? 'var(--color-text-inverse)'
                    : 'var(--color-text-secondary)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  fontWeight: 600,
                }}
              >
                {s ? t(`approval.status.${s}`) : t('approval.inbox.tab.admin')}
              </button>
            ))}
          </div>

          <RequestsTable
            query={adminQuery}
            columns={requestColumns}
            emptyKey="approval.inbox.empty.admin"
            page={adminPage}
            onPageChange={handleAdminPageChange}
            onRowClick={(id) => navigate(`/approval-center/${id}`)}
            i18nLocale={i18n.language}
            t={t}
            showToast={showToast}
          />
        </div>
      )}

      {/* CommonUsagePanel §6.8 */}
      <ApprovalCommonUsagePanel />

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 520px; }
        }
      `}</style>
    </div>
  );
}

// ─── 재사용 가능한 결재 요청 목록 테이블 ──────────────────────────────────────

interface RequestsTableProps {
  query: ReturnType<typeof useMyRequests | typeof useApprovalInbox>;
  columns: string[];
  emptyKey: string;
  page: number;
  onPageChange: (p: number) => void;
  onRowClick: (id: number) => void;
  i18nLocale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  showToast: (msg: string) => void;
}

function RequestsTable({
  query,
  columns,
  emptyKey,
  page,
  onPageChange,
  onRowClick,
  i18nLocale,
  t,
}: RequestsTableProps) {
  const pageData = query.data;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const isEmpty = !isLoading && !isError && (pageData?.empty ?? true);
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;

  return (
    <div
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
      }}
    >
      {/* Loading */}
      {isLoading && (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          aria-busy="true"
        >
          <thead>
            <tr style={{ background: 'var(--color-surface-muted)' }}>
              {columns.map((h) => <th key={h} style={thStyle}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
          </tbody>
        </table>
      )}

      {/* 오류 */}
      {isError && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <ErrorState
            title={t('approval.error.network')}
            message={(query.error as unknown as ApiError)?.message ?? t('approval.error.network')}
            onRetry={() => void query.refetch()}
          />
        </div>
      )}

      {/* 빈 상태 */}
      {isEmpty && (
        <div style={{ padding: 'var(--spacing-6)' }}>
          <EmptyState
            title={t(emptyKey)}
            description={t(emptyKey)}
          />
        </div>
      )}

      {/* 기본 */}
      {showTable && pageData && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr style={{ background: 'var(--color-surface-muted)' }}>
                  {columns.map((h) => <th key={h} style={thStyle}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {pageData.content.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    role="button"
                    aria-label={row.title}
                    onClick={() => onRowClick(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row.id);
                      }
                    }}
                    style={rowStyle}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = '';
                    }}
                  >
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        {row.formLabel}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                        {row.title}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        {row.requesterName ?? row.requester}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <StatusPill tone={statusTone(row.status)}>
                        {t(`approval.status.${row.status}`, { defaultValue: row.statusLabel })}
                      </StatusPill>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
                        {formatDate(row.submittedAt, i18nLocale)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              borderTop: '1px solid var(--color-surface-border)',
              flexWrap: 'wrap',
              gap: 'var(--spacing-2)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {pageData.number * pageData.size + 1}–{Math.min((pageData.number + 1) * pageData.size, pageData.totalElements)} / {pageData.totalElements.toLocaleString()}건
            </span>
            <nav aria-label="페이지 탐색" style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
              <button
                type="button"
                disabled={pageData.first}
                onClick={() => onPageChange(page - 1)}
                aria-label="이전 페이지"
                style={paginationBtnStyle(pageData.first)}
              >
                &lt;
              </button>
              {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPageChange(i)}
                  aria-label={`${i + 1}페이지`}
                  aria-current={i === pageData.number ? 'page' : undefined}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                    border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                    color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontWeight: i === pageData.number ? 600 : 400,
                  }}
                >
                  {i + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={pageData.last}
                onClick={() => onPageChange(page + 1)}
                aria-label="다음 페이지"
                style={paginationBtnStyle(pageData.last)}
              >
                &gt;
              </button>
            </nav>
          </div>
        </>
      )}
    </div>
  );
}

// ─── 공통 스타일 상수 ──────────────────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-3) var(--spacing-4)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-4)',
  borderBottom: '1px solid var(--color-surface-border)',
};

const rowStyle: React.CSSProperties = {
  cursor: 'pointer',
  transition: 'background 0.1s',
};

const paginationBtnStyle = (disabled: boolean): React.CSSProperties => ({
  minWidth: 28,
  height: 28,
  padding: '0 var(--spacing-2)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-xs)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});
